import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from '@env/environment';
import {
    ApplicationCookie,
    CookieService,
    CustomerProfile,
    EntityService,
    GAService,
    LoaderService,
    ProfileService,
    SessionService,
    LoaderComponent,
    SnackBarManagerComponent,
} from '@le_cedre/portal-lib';
import { TranslateService } from '@ngx-translate/core';
import Cookie from 'js-cookie';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { APPLICATION_NAME } from './shared/utils/app.utils';

const ALLOWED_LANGUAGES = ['fr', 'pl'];

@Component({
    selector: 'app-root',
    template: `
        <router-outlet></router-outlet>
        <portal-loader [commonUrl]="commonUrl"></portal-loader>
        <portal-snack-bar-manager></portal-snack-bar-manager>
    `,
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [RouterOutlet, LoaderComponent, SnackBarManagerComponent],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
    selectedEntityId$: Observable<string>;
    customerProfile$: Observable<CustomerProfile>;
    preferredApp$: Observable<string>;

    destroy$ = new Subject();
    currentLang: string;
    commonUrl = environment.urls.commonStatic;

    private appCookie: ApplicationCookie;

    constructor(
        private activeRoute: ActivatedRoute,
        private cdRef: ChangeDetectorRef,
        private cookieService: CookieService,
        private loader: LoaderService,
        @Inject(Router) private router: Router,
        private session: SessionService,
        private titleService: Title,
        @Inject(TranslateService) private translateService: TranslateService,
        private ga: GAService,
        private entity: EntityService,
        private profile: ProfileService
    ) {
        this.currentLang = this.getBrowserLang();
        this.selectedEntityId$ = this.entity.selectedEntityId$;
        this.customerProfile$ = this.profile.customerProfile$;
        this.preferredApp$ = this.profile.preferedApp$;
        this.translateService.use(this.currentLang);
        this.loader.fetchHtml();
    }

    ngOnInit() {
        document.body.setAttribute('class', 'default'); // init default for login/entity-selection/consent-page
        const windowPathName = window.location.pathname;
        const shouldRedirect = windowPathName.includes('/backoffice') || windowPathName.includes('/consent-page');
        this.cookieService.getToken({ redirect: shouldRedirect });

        this.activeRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(({ redirect_url }) => {
            if (!!redirect_url) {
                const domain = environment.domain;
                Cookie.set('redirect_url', redirect_url, { domain });
            }
        });

        this.router.events.subscribe((val) => {
            if (val instanceof NavigationEnd) {
                const { url } = val;
                this.ga.sendPageEvent({
                    url,
                    extras: { dimension12: url },
                });
            }
        });

        // Attendre que les cookies soient chargés dans le store avant d'appeler fetchProfile
        combineLatest([this.cookieService.accessToken$, this.cookieService.appName$])
            .pipe(
                takeUntil(this.destroy$),
                filter(([_, appCookie]) => !!appCookie)

                /*
                // Filtrer : appCookie doit exister ET on doit avoir un uid dans les cookies
                filter(([_, appCookie]) => {
                    if (!appCookie) return false;
                    // Vérifier que l'uid existe dans les cookies avant de continuer
                    const uid =
                        Cookie.get('uId') || Cookie.get('de_uId') || Cookie.get('ea_uId') || Cookie.get('ef_uId');
                    return !!uid;
                }),
                take(1) // Ne s'exécute qu'une seule fois pour éviter les boucles infinies
                */
            )
            .subscribe(([accessToken, cookieAppName]) => {
                if (windowPathName.includes('backoffice')) {
                    this.translateService.get(`PORTAL_APP_TITLE_${cookieAppName}`).subscribe((title) => {
                        this.titleService.setTitle(title);
                    });
                }
                // used for login as collab on digicedra
                if (window.location.pathname.includes('login/collab') && window.location.search) {
                    return;
                }

                // used for login on digierp
                if (
                    !!accessToken &&
                    (window.location.pathname.includes('login/collab') || cookieAppName === APPLICATION_NAME.digierp)
                ) {
                    this.session.fetchProfileByUidCollab(null, accessToken);
                }
                /*
                // Note: On ne vérifie plus !!accessToken car les tokens spécifiques (access_token_de)
                // peuvent exister même si le token principal est vide
                if (window.location.pathname.includes('login/collab') || cookieAppName === APPLICATION_NAME.digierp) {
                    this.session.fetchProfileByUidCollab(null, null);
                }
                */

                // used for login on digicedra and digicedrf
                else if (
                    !!accessToken &&
                    (cookieAppName === APPLICATION_NAME.digicedra || cookieAppName === APPLICATION_NAME.digicedrf)
                ) {
                    this.session.fetchProfileByToken(accessToken, null);
                }
                /*
                else if (cookieAppName === APPLICATION_NAME.digicedra || cookieAppName === APPLICATION_NAME.digicedrf) {
                    this.session.fetchProfileByToken(null, null);
                }
                */
            });

        const cookieTmp = Cookie.get('application');

        /* set appName from login, login/collab and connect as
         * appName set -> go to appName
         * login -> EA
         * login/collab without context-> DE
         * connect as with accessToken -> EA
         */
        if (!!cookieTmp && !window.location.pathname.includes('login/collab')) {
            this.appCookie = JSON.parse(cookieTmp);
            document.body.setAttribute('class', this.appCookie.appName);
            this.cookieService.setAppInfos({ appInfos: this.appCookie });
        } else {
            if (!window.location.pathname.includes('login/collab')) {
                this.appCookie = {
                    appName: APPLICATION_NAME.digicedra,
                    host: environment.apps[APPLICATION_NAME.digicedra],
                };
            } else {
                if (window.location.search.includes('accessToken=')) {
                    this.appCookie = {
                        appName: APPLICATION_NAME.digicedra,
                        host: environment.apps[APPLICATION_NAME.digicedra],
                    };
                } else {
                    this.appCookie = {
                        appName: APPLICATION_NAME.digierp,
                        host: environment.apps[APPLICATION_NAME.digierp],
                    };
                }
            }
            const domain = environment.domain;
            Cookie.set('application', JSON.stringify(this.appCookie), { domain });
            document.body.setAttribute('class', this.appCookie.appName);
            this.cookieService.setAppInfos({ appInfos: this.appCookie });
        }
    }

    ngAfterViewInit(): void {
        this.loader.fetchHtmlSuccess();
        this.cdRef.detectChanges();
    }

    ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }

    getBrowserLang() {
        let lang = this.getLangFromUrl();

        if (!lang) {
            const browserLang = navigator.language;
            lang = browserLang?.split('-')[0];
        }

        return ALLOWED_LANGUAGES.includes(lang) ? lang : 'fr';
    }

    private getLangFromUrl(): string | null {
        const url = new URL(window.location.href);
        return url.searchParams.get('lang')?.toLowerCase();
    }
}
